import React, { Component } from 'react';
import { formatCurrency } from '../utils/formatCurrency';
interface Receipt2Props {
  receiptNo: string;
  receiptDate: string;
  bookingRef: string;
  gstRegNo?: string;
  tourCode?: string;
  paymentMethod: string;
  collectedBy: string;
  printedBy: string;
  currency: string;
  amountReceived: number;
  totalAmount: number;
  balanceDue: number;
  customerName: string;
  paymentTime: string;
  remarks: string;
  showBarcode?: boolean;
  companyInfo: {
    name: string;
    logo: string;
    address: string;
    contact: string;
    uen: string;
  };
}
// Receipt Section Component
function ReceiptSection({
  receiptNo,
  receiptDate,
  bookingRef,
  gstRegNo,
  tourCode,
  paymentMethod,
  printedBy,
  amountReceived,
  totalAmount,
  balanceDue,
  customerName,
  paymentTime,
  collectedBy,
  remarks,
  currency
}: Receipt2Props) {
  return <>
      {/* ZONE 2 - Receipt Identification (Full Width Info Box) */}
      <div className="mb-6" data-id="element-1040">
        <div className="border border-neutral-300 rounded p-4 bg-neutral-50" data-id="element-1041">
          <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-1042">
            {/* Left Column */}
            <div className="space-y-2" data-id="element-1043">
              <div className="flex justify-between" data-id="element-1044">
                <span className="text-[10px] font-medium text-neutral-600" data-id="element-1045">
                  Receipt No.:
                </span>
                <span className="text-[10px] font-bold text-neutral-900" data-id="element-1046">
                  {receiptNo}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-1047">
                <span className="text-[10px] font-medium text-neutral-600" data-id="element-1048">
                  Receipt Date:
                </span>
                <span className="text-[10px] text-neutral-900" data-id="element-1049">
                  {receiptDate}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-1050">
                <span className="text-[10px] font-medium text-neutral-600" data-id="element-1051">
                  Booking Ref:
                </span>
                <span className="text-[10px] text-neutral-900" data-id="element-1052">
                  {bookingRef}
                </span>
              </div>
              {gstRegNo && <div className="flex justify-between" data-id="element-1053">
                  <span className="text-[10px] font-medium text-neutral-600" data-id="element-1054">
                    GST Reg. No.:
                  </span>
                  <span className="text-[10px] text-neutral-900" data-id="element-1055">
                    {gstRegNo}
                  </span>
                </div>}
            </div>

            {/* Right Column */}
            <div className="space-y-2" data-id="element-1056">
              {tourCode && <div className="flex justify-between" data-id="element-1057">
                  <span className="text-[10px] font-medium text-neutral-600" data-id="element-1058">
                    Tour Code:
                  </span>
                  <span className="text-[10px] text-neutral-900" data-id="element-1059">
                    {tourCode}
                  </span>
                </div>}
              <div className="flex justify-between" data-id="element-1060">
                <span className="text-[10px] font-medium text-neutral-600" data-id="element-1061">
                  Payment Method:
                </span>
                <span className="text-[10px] text-neutral-900" data-id="element-1062">
                  {paymentMethod}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-1063">
                <span className="text-[10px] font-medium text-neutral-600" data-id="element-1064">
                  Printed By:
                </span>
                <span className="text-[10px] text-neutral-900" data-id="element-1065">
                  {printedBy}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ZONE 3 - Financial Summary */}
      <div className="text-center mb-6 py-4 border-y border-neutral-300 bg-neutral-50" data-id="element-1066">
        <div className="space-y-2" data-id="element-1067">
          <div className="flex justify-center items-baseline gap-3" data-id="element-1068">
            <span className="text-sm font-semibold text-neutral-600" data-id="element-1069">
              Amount Received:
            </span>
            <span className="text-3xl font-bold text-green-600" data-id="element-1070">
              {currency} {formatCurrency(amountReceived, false)}
            </span>
          </div>
          <div className="flex justify-center items-center gap-6" data-id="element-1071">
            <div className="flex items-baseline gap-2" data-id="element-1072">
              <span className="text-xs text-neutral-600" data-id="element-1073">Total Amount:</span>
              <span className="text-sm font-semibold text-neutral-900" data-id="element-1074">
                {currency} {formatCurrency(totalAmount, false)}
              </span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-1075">
              <span className="text-xs text-neutral-600" data-id="element-1076">Balance Due:</span>
              <span className={`text-sm font-semibold ${balanceDue > 0 ? 'text-red-600' : 'text-neutral-900'}`} data-id="element-1077">
                {currency} {formatCurrency(balanceDue, false)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ZONE 4 - Transaction Details */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-6" data-id="element-1078">
        <div data-id="element-1079">
          <span className="text-[10px] font-semibold text-neutral-600" data-id="element-1080">
            Received From:
          </span>
          <p className="text-sm font-medium text-neutral-900 mt-0.5" data-id="element-1081">
            {customerName}
          </p>
        </div>
        <div data-id="element-1082">
          <span className="text-[10px] font-semibold text-neutral-600" data-id="element-1083">
            Payment Method:
          </span>
          <p className="text-sm text-neutral-900 mt-0.5" data-id="element-1084">{paymentMethod}</p>
        </div>
        <div data-id="element-1085">
          <span className="text-[10px] font-semibold text-neutral-600" data-id="element-1086">
            Payment Time:
          </span>
          <p className="text-sm text-neutral-900 mt-0.5" data-id="element-1087">{paymentTime}</p>
        </div>
        <div data-id="element-1088">
          <span className="text-[10px] font-semibold text-neutral-600" data-id="element-1089">
            Collected By:
          </span>
          <p className="text-sm font-medium text-neutral-900 mt-0.5" data-id="element-1090">
            {collectedBy}
          </p>
        </div>
        {remarks && <div className="col-span-2" data-id="element-1091">
            <span className="text-[10px] font-semibold text-neutral-600" data-id="element-1092">
              Remarks:
            </span>
            <p className="text-sm text-neutral-900 mt-0.5" data-id="element-1093">{remarks}</p>
          </div>}
      </div>
    </>;
}
export function Receipt2(props: Receipt2Props) {
  const currentDateTime = new Date().toLocaleString('en-SG', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  return <div className="h-full overflow-auto bg-gradient-to-br from-neutral-50 to-neutral-100 p-6" data-id="element-1094">
      <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg p-8" data-id="element-1095">
        {/* ZONE 1 - HEADER */}
        <div className="mb-6 pb-4 border-b border-neutral-300" data-id="element-1096">
          <div className="flex justify-between items-start" data-id="element-1097">
            {/* Left: Logo */}
            <div data-id="element-1098">
              <img src={props.companyInfo.logo} alt={props.companyInfo.name} className="h-12 object-contain" data-id="element-1099" />
            </div>

            {/* Right: Company Info */}
            <div className="text-right" data-id="element-1100">
              <h1 className="text-sm font-bold text-[#1a365d] mb-1" data-id="element-1101">
                {props.companyInfo.name}
              </h1>
              <div className="text-[9px] text-neutral-600 space-y-0.5" data-id="element-1102">
                <p data-id="element-1103">{props.companyInfo.address}</p>
                <p data-id="element-1104">{props.companyInfo.contact}</p>
                <p data-id="element-1105">UEN: {props.companyInfo.uen}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-[#1a365d] text-center mb-6" data-id="element-1106">
          OFFICIAL RECEIPT
        </h2>

        {/* Main Receipt Content */}
        <ReceiptSection {...props} data-id="element-1107" />

        {/* ZONE 5 - Terms & Conditions */}
        <div className="mb-6 p-3 bg-neutral-100 border border-neutral-300 rounded" data-id="element-1108">
          <h3 className="text-[10px] font-bold text-neutral-900 mb-2" data-id="element-1109">
            TERMS & CONDITIONS
          </h3>
          <div className="text-[8px] text-neutral-700 space-y-1 leading-relaxed" data-id="element-1110">
            <p data-id="element-1111">
              • <strong data-id="element-1112">Payment Clearance:</strong> Payment accepted via cash,
              bank transfer, cheque, or credit card (2% processing fee applies).
              Cheques subject to clearance.
            </p>
            <p data-id="element-1113">
              • <strong data-id="element-1114">Deposit Policy:</strong> All deposits are non-refundable
              and non-transferable unless otherwise stated in booking terms.
            </p>
            <p data-id="element-1115">
              • <strong data-id="element-1116">Postponement:</strong> Any postponement of travel dates
              will be treated as a cancellation and subject to cancellation
              fees.
            </p>
            <p data-id="element-1117">
              • <strong data-id="element-1118">Full Terms:</strong> This receipt is subject to our
              complete Booking Terms & Conditions available upon request.
            </p>
          </div>
        </div>

        {/* ZONE 6 - Footer with Optional Barcode */}
        <div className="text-center space-y-3" data-id="element-1119">
          <div className="space-y-1" data-id="element-1120">
            <p className="text-[9px] text-neutral-500" data-id="element-1121">
              Generated by system on {currentDateTime}
            </p>
            <p className="text-[9px] text-neutral-500 italic" data-id="element-1122">
              This is a computer-generated document. No signature is required.
            </p>
          </div>

          {/* Optional Barcode - Doesn't affect layout when hidden */}
          {props.showBarcode !== false && <div className="flex flex-col items-center pt-3 border-t border-neutral-200" data-id="element-1123">
              <div className="font-mono text-[9px] mb-1 text-neutral-500" data-id="element-1124">
                {props.receiptNo}
              </div>
              <svg className="w-48 h-12" viewBox="0 0 200 60" xmlns="http://www.w3.org/2000/svg" data-id="element-1125">
                {/* Simple barcode representation */}
                <rect x="10" y="5" width="2" height="50" fill="#000" data-id="element-1126" />
                <rect x="15" y="5" width="4" height="50" fill="#000" data-id="element-1127" />
                <rect x="22" y="5" width="2" height="50" fill="#000" data-id="element-1128" />
                <rect x="27" y="5" width="6" height="50" fill="#000" data-id="element-1129" />
                <rect x="36" y="5" width="2" height="50" fill="#000" data-id="element-1130" />
                <rect x="41" y="5" width="4" height="50" fill="#000" data-id="element-1131" />
                <rect x="48" y="5" width="2" height="50" fill="#000" data-id="element-1132" />
                <rect x="53" y="5" width="2" height="50" fill="#000" data-id="element-1133" />
                <rect x="58" y="5" width="6" height="50" fill="#000" data-id="element-1134" />
                <rect x="67" y="5" width="2" height="50" fill="#000" data-id="element-1135" />
                <rect x="72" y="5" width="4" height="50" fill="#000" data-id="element-1136" />
                <rect x="79" y="5" width="2" height="50" fill="#000" data-id="element-1137" />
                <rect x="84" y="5" width="2" height="50" fill="#000" data-id="element-1138" />
                <rect x="89" y="5" width="6" height="50" fill="#000" data-id="element-1139" />
                <rect x="98" y="5" width="4" height="50" fill="#000" data-id="element-1140" />
                <rect x="105" y="5" width="2" height="50" fill="#000" data-id="element-1141" />
                <rect x="110" y="5" width="2" height="50" fill="#000" data-id="element-1142" />
                <rect x="115" y="5" width="6" height="50" fill="#000" data-id="element-1143" />
                <rect x="124" y="5" width="2" height="50" fill="#000" data-id="element-1144" />
                <rect x="129" y="5" width="4" height="50" fill="#000" data-id="element-1145" />
                <rect x="136" y="5" width="2" height="50" fill="#000" data-id="element-1146" />
                <rect x="141" y="5" width="6" height="50" fill="#000" data-id="element-1147" />
                <rect x="150" y="5" width="2" height="50" fill="#000" data-id="element-1148" />
                <rect x="155" y="5" width="4" height="50" fill="#000" data-id="element-1149" />
                <rect x="162" y="5" width="2" height="50" fill="#000" data-id="element-1150" />
                <rect x="167" y="5" width="2" height="50" fill="#000" data-id="element-1151" />
                <rect x="172" y="5" width="6" height="50" fill="#000" data-id="element-1152" />
                <rect x="181" y="5" width="4" height="50" fill="#000" data-id="element-1153" />
                <rect x="188" y="5" width="2" height="50" fill="#000" data-id="element-1154" />
              </svg>
            </div>}
        </div>
      </div>
    </div>;
}